// frontend/src/modules/invoicing/pages/SettingsPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Building2, FileText, Palette, BellRing, Percent, AlarmClock, Trash2, Star } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { useApi } from '../../../design-system/useApi';
import { settingsApi, taxProfilesApi, TenantSettingsPatch } from '../api';
import { VISUAL } from '../../../design-system/visual';
import type { TaxProfile } from '../types';

export function SettingsPage() {
  const { tenant, reloadTenant } = useBranding();
  if (!tenant) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Workspace"
        title="Settings"
        description="Company info, invoice defaults, branding, and notifications — all in one place."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-14 space-y-8 max-w-5xl">
        <SettingsSection
          icon={<Building2 className="h-4 w-4" strokeWidth={1.8} />}
          title="Company info"
          description="Shown on every invoice PDF and outbound email."
          fields={['company_name', 'company_tagline', 'company_address', 'company_email', 'company_phone', 'company_website']}
          onSaved={reloadTenant}
        />
        <SettingsSection
          icon={<FileText className="h-4 w-4" strokeWidth={1.8} />}
          title="Invoice defaults"
          description="Sensible defaults applied to new invoices and recurring templates."
          fields={['invoice_number_prefix', 'default_invoice_due_days', 'default_tax_rate']}
          onSaved={reloadTenant}
        />
        <SettingsSection
          icon={<Palette className="h-4 w-4" strokeWidth={1.8} />}
          title="Branding"
          description="Visible on your invoice PDFs and your dashboard."
          fields={['company_logo_url', 'company_accent_color']}
          onSaved={reloadTenant}
        />
        <SettingsSection
          icon={<BellRing className="h-4 w-4" strokeWidth={1.8} />}
          title="Notifications"
          description="Where and how often we ping the account owner."
          fields={['owner_notification_email', 'owner_notification_mode']}
          onSaved={reloadTenant}
        />
        <SettingsSection
          icon={<AlarmClock className="h-4 w-4" strokeWidth={1.8} />}
          title="Payment reminders"
          description="When enabled, clients with overdue invoices get a polite nudge by email 1, 7, and 14 days after the due date (never more than one every 3 days)."
          fields={['payment_reminders_enabled']}
          onSaved={reloadTenant}
        />
        <TaxProfilesSection />
      </div>
    </div>
  );
}

interface SettingsSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  fields: Array<keyof TenantSettingsPatch>;
  onSaved: () => void;
}

function SettingsSection({ icon, title, description, fields, onSaved }: SettingsSectionProps) {
  const { tenant } = useBranding();
  const [draft, setDraft] = React.useState<TenantSettingsPatch>({});
  const [saving, setSaving] = React.useState(false);

  function valueOf(key: keyof TenantSettingsPatch): any {
    if (key in draft) return (draft as any)[key];
    const t = tenant as any;
    return t?.[key] ?? '';
  }

  function set<K extends keyof TenantSettingsPatch>(key: K, value: TenantSettingsPatch[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function save() {
    if (Object.keys(draft).length === 0) return;
    setSaving(true);
    try {
      await settingsApi.update(draft);
      toast.success(`${title} saved`);
      setDraft({});
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  const dirty = Object.keys(draft).length > 0;

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
      <header className="lg:pt-1">
        <div className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 text-saffron-foreground mb-3">
          {icon}
        </div>
        <h2 className={VISUAL.dashSection}>{title}</h2>
        <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
      </header>
      <div className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
        <div className="grid gap-5">
          {fields.map((key) => (
            <SettingField key={key} fieldKey={key} value={valueOf(key)} onChange={(v) => set(key, v)} />
          ))}
        </div>
        <div className="flex items-center justify-between mt-7 pt-5 border-t border-border/40">
          <p className="text-[12.5px] text-muted-foreground">
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </p>
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full h-10 px-5 font-semibold text-[13px]"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </section>
  );
}

// Per-field renderer. Keep all field metadata here so the section components stay simple.
function SettingField({ fieldKey, value, onChange }: { fieldKey: keyof TenantSettingsPatch; value: any; onChange: (v: any) => void }) {
  switch (fieldKey) {
    case 'company_address':
      return <Field label="Company address"><Textarea rows={2} value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
    case 'company_tagline':
      return <Field label="Tagline"><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="One-liner shown under company name" /></Field>;
    case 'company_email':
      return <Field label="Billing email"><Input type="email" value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
    case 'company_phone':
      return <Field label="Phone"><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
    case 'company_website':
      return <Field label="Website"><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="https://…" /></Field>;
    case 'invoice_number_prefix':
      return <Field label="Invoice number prefix" hint="Combined with a per-tenant sequence — e.g. INV-000123."><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
    case 'default_invoice_due_days':
      return <Field label="Default due (days from issue)"><Input type="number" min="0" max="365" value={value ?? 14} onChange={(e) => onChange(Number(e.target.value))} /></Field>;
    case 'default_tax_rate':
      return <Field label="Default tax rate (%)"><Input type="number" min="0" max="100" step="0.01" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} /></Field>;
    case 'company_logo_url':
      return <Field label="Logo URL" hint="Direct link to a hosted image. Prefer uploading a file instead? Use Workspace settings → Company."><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="https://…/logo.png" /></Field>;
    case 'company_accent_color':
      return <Field label="Accent color (hex)" hint="Used in your dashboard and invoice PDFs."><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="#0066FF" /></Field>;
    case 'owner_notification_email':
      return <Field label="Notification email"><Input type="email" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="owner@yourcompany.com" /></Field>;
    case 'owner_notification_mode':
      return (
        <Field label="Notification frequency">
          <Select value={value ?? 'instant'} onValueChange={onChange}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Instant — email per event</SelectItem>
              <SelectItem value="hourly">Hourly digest</SelectItem>
              <SelectItem value="daily">Daily digest</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      );
    case 'company_name':
      return <Field label="Company name"><Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} /></Field>;
    case 'payment_reminders_enabled':
      return (
        <Field label="Automated client reminders" hint="Runs daily. Reminders stop the moment an invoice is paid, credited out, or cancelled.">
          <Select value={value === true ? 'on' : 'off'} onValueChange={(v) => onChange(v === 'on')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off — I'll chase invoices myself</SelectItem>
              <SelectItem value="on">On — email overdue clients automatically</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      );
  }
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/* ───────────────────────── Tax profiles (036) ───────────────────────── */

function TaxProfilesSection() {
  const { data, loading, reload } = useApi<{ tax_profiles: TaxProfile[] }>('/api/invoicing/tax-profiles');
  const profiles = data?.tax_profiles ?? [];
  const [name, setName] = React.useState('');
  const [rate, setRate] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const r = Number(rate);
    if (!name.trim()) { toast.error('Give the profile a name'); return; }
    if (!(r >= 0 && r <= 100)) { toast.error('Rate must be between 0 and 100'); return; }
    setBusy(true);
    try {
      await taxProfilesApi.create({ name: name.trim(), rate_percent: r, is_default: profiles.length === 0 });
      toast.success('Tax profile added');
      setName(''); setRate('');
      reload();
    } catch (err: any) {
      toast.error('Could not add profile', { description: err.message });
    } finally { setBusy(false); }
  }

  async function makeDefault(p: TaxProfile) {
    try {
      await taxProfilesApi.update(p.id, { is_default: true });
      toast.success(`${p.name} is now the default`);
      reload();
    } catch (err: any) {
      toast.error('Could not update', { description: err.message });
    }
  }

  async function remove(p: TaxProfile) {
    try {
      await taxProfilesApi.remove(p.id);
      toast.success(`${p.name} removed`);
      reload();
    } catch (err: any) {
      toast.error('Could not remove', { description: err.message });
    }
  }

  return (
    <section className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
      <header className="lg:pt-1">
        <div className="inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 text-saffron-foreground mb-3">
          <Percent className="h-4 w-4" strokeWidth={1.8} />
        </div>
        <h2 className={VISUAL.dashSection}>Tax profiles</h2>
        <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">
          Named rates (VAT, GST, exempt…) you can pick per invoice. A profile overrides every line's tax rate; the default is preselected on new documents.
        </p>
      </header>
      <div className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
        {loading ? (
          <p className="text-[13px] text-muted-foreground">Loading…</p>
        ) : profiles.length === 0 ? (
          <p className="text-[13px] text-muted-foreground mb-5">
            No tax profiles yet. Your flat default rate keeps applying until you add one.
          </p>
        ) : (
          <ul className="divide-y divide-border/40 mb-5">
            {profiles.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-semibold text-foreground truncate">
                    {p.name}
                    {p.is_default && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.1em] font-bold text-saffron-foreground">
                        <Star className="h-3 w-3" /> Default
                      </span>
                    )}
                  </p>
                </div>
                <span className="text-[13px] font-mono tabular-nums text-muted-foreground">{Number(p.rate_percent)}%</span>
                {!p.is_default && (
                  <Button type="button" variant="ghost" size="sm" className="h-8 px-2.5 text-[12px]" onClick={() => makeDefault(p)}>
                    Make default
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => remove(p)}
                  className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  aria-label={`Delete ${p.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 pt-4 border-t border-border/40">
          <div className="grid gap-1.5 flex-1 min-w-[180px]">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VAT (standard)" />
          </div>
          <div className="grid gap-1.5 w-[120px]">
            <Label>Rate (%)</Label>
            <Input type="number" min="0" max="100" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="20" />
          </div>
          <Button type="submit" disabled={busy} className="h-10 rounded-full px-5 text-[13px] font-semibold">
            {busy ? 'Adding…' : 'Add profile'}
          </Button>
        </form>
      </div>
    </section>
  );
}
